import React, { useEffect, useMemo, useState } from 'react';
import { Ball, ChanceyCard, GameBadge, Icon } from '../../components/chancey';
import { safeAreaPadding } from '../../components/layout/safeArea';
import { ApiError } from '../../api/client';
import { useGameApi } from '../../api/games';
import type { FrequencyStatsResponse, PairingsStatsResponse } from '../../api/types';
import { useActiveGame } from '../../hooks/useActiveGame';
import { useUserPrefs } from '../../hooks/useUserPrefs';
import { useChanceyTheme } from '../../theme/ThemeContext';

/** Must match Worker allowlist in `gameStatsFrequency.ts` / `gameStatsPairings.ts` (25, 50, 100, 200, 500). */
export const SIMPLE_STATS_WINDOW = 200;

function buildVibeCopy(
  freq: FrequencyStatsResponse | null,
  pairStats: PairingsStatsResponse | null
): { headline: string; body: string } {
  const draws = freq?.drawsCount ?? 0;
  const hotBall = freq?.hot?.[0];
  const topPair = pairStats?.pairs?.[0];
  if (!freq || draws === 0) {
    return {
      headline: 'Fresh numbers',
      body: 'Once stats load, we summarize what stood out in recent draws — for fun, not forecasts.',
    };
  }
  const bits: string[] = [];
  if (hotBall) {
    bits.push(
      `Number ${hotBall.n} appears most often in this window (${hotBall.count} times among main balls).`
    );
  }
  if (topPair) {
    bits.push(
      `${topPair.a} and ${topPair.b} landed together ${topPair.count} times — about ×${topPair.lift.toFixed(1)} vs random pairing.`
    );
  }
  return {
    headline: hotBall ? 'Highlights' : 'This window',
    body:
      bits.join(' ') ||
      'Past draws don’t predict future ones — these are summaries of history only.',
  };
}

export const StatsSimpleView: React.FC = () => {
  const { c, typ, d } = useChanceyTheme();
  const { game } = useActiveGame();
  const { setPartial } = useUserPrefs();
  const { getFrequencyStats, getPairingsStats } = useGameApi();

  const [freqStats, setFreqStats] = useState<FrequencyStatsResponse | null>(null);
  const [freqLoading, setFreqLoading] = useState(true);
  const [freqError, setFreqError] = useState<string | null>(null);

  const [pairStats, setPairStats] = useState<PairingsStatsResponse | null>(null);
  const [pairLoading, setPairLoading] = useState(true);
  const [pairError, setPairError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFreqLoading(true);
    setFreqError(null);
    getFrequencyStats(game.id, SIMPLE_STATS_WINDOW)
      .then((res) => {
        if (!cancelled) setFreqStats(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          console.error('[Chancey stats] simple frequency API error', e);
        }
        setFreqStats(null);
        setFreqError(e instanceof Error ? e.message : 'Could not load stats');
      })
      .finally(() => {
        if (!cancelled) setFreqLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [game.id, getFrequencyStats]);

  useEffect(() => {
    let cancelled = false;
    setPairLoading(true);
    setPairError(null);
    getPairingsStats(game.id, SIMPLE_STATS_WINDOW)
      .then((res) => {
        if (!cancelled) setPairStats(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          console.error('[Chancey stats] simple pairings API error', e);
        }
        setPairStats(null);
        setPairError(e instanceof Error ? e.message : 'Could not load pairings');
      })
      .finally(() => {
        if (!cancelled) setPairLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [game.id, getPairingsStats]);

  const vibe = useMemo(() => buildVibeCopy(freqStats, pairStats), [freqStats, pairStats]);

  const hottest = useMemo(() => {
    if (!freqStats?.frequency?.length) return null;
    return [...freqStats.frequency].sort((a, b) => b.count - a.count)[0];
  }, [freqStats]);

  const overdueTop = freqStats?.overdue?.[0] ?? null;

  const topPair = pairStats?.pairs?.filter((p) => Number.isFinite(p.lift))[0] ?? null;

  const expectedApprox = freqStats
    ? (freqStats.drawsCount * freqStats.game.mainCount) / freqStats.game.mainRange
    : 0;
  const pctAboveAvg =
    hottest && expectedApprox > 0 ? Math.round(((hottest.count / expectedApprox) - 1) * 100) : null;

  const showSkeleton = freqLoading && !freqStats;
  const shimmerStyle: React.CSSProperties = useMemo(
    () => ({
      background: `linear-gradient(90deg, ${c.surfaceAlt} 0%, ${c.surface} 50%, ${c.surfaceAlt} 100%)`,
      backgroundSize: '200% 100%',
      animation: 'chanceyShimmer 1.2s ease-in-out infinite',
    }),
    [c.surface, c.surfaceAlt]
  );

  const loadingBlock = freqLoading || pairLoading;

  return (
    <div
      className="flex flex-col"
      style={{ ...safeAreaPadding(8, d.pad, 16, d.pad), gap: d.gap, color: c.ink }}
    >
      <style>{`
        @keyframes chanceyShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div className="flex flex-col items-start gap-1.5 px-0.5 pb-0.5 pt-1">
        <GameBadge game={game} />
        <div
          className="text-[32px] leading-tight"
          style={{
            fontFamily: typ.display,
            fontWeight: typ.displayWeight,
            letterSpacing: typ.displayLetter,
          }}
        >
          Today&apos;s reading
        </div>
      </div>

      <p className="max-w-[320px] text-[13px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
        Three things worth knowing before the next draw.
      </p>

      {freqError ? (
        <div className="text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
          {freqError}
        </div>
      ) : null}
      {pairError ? (
        <div className="text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
          {pairError}
        </div>
      ) : null}

      <div
        className="relative overflow-hidden rounded-[24px] p-5 text-white"
        style={{
          background: c.gradient,
          boxShadow: `0 12px 32px ${c.accent}40`,
        }}
      >
        <div
          className="pointer-events-none absolute -right-8 -top-8 h-[140px] w-[140px] rounded-full opacity-15"
          style={{ background: '#fff' }}
        />
        <div
          className="pointer-events-none absolute -bottom-10 -left-5 h-[100px] w-[100px] rounded-full opacity-10"
          style={{ background: '#fff' }}
        />
        <div className="relative">
          <div className="mb-2.5 flex items-center gap-1.5">
            {Icon.flame(14, '#fff')}
            <div
              className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/85"
              style={{ fontFamily: typ.body }}
            >
              Vibe of the draw
            </div>
          </div>
          {showSkeleton ? (
            <div className="space-y-2">
              <div className="max-w-[280px] rounded-lg opacity-30" style={{ width: '75%', height: 32, background: '#fff' }} />
              <div className="h-4 w-full max-w-[320px] rounded opacity-25" style={{ background: '#fff' }} />
            </div>
          ) : (
            <>
              <div
                className="mb-2 text-[28px] leading-tight"
                style={{
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  letterSpacing: typ.displayLetter,
                }}
              >
                {vibe.headline}
              </div>
              <p className="text-[14px] leading-relaxed text-white/92" style={{ fontFamily: typ.body }}>
                {vibe.body}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <ChanceyCard c={c} pad={16}>
          <div className="flex items-center gap-3.5">
            {showSkeleton ? (
              <div className="h-14 w-14 shrink-0 rounded-full" style={shimmerStyle} />
            ) : hottest ? (
              <Ball n={hottest.n} size={56} c={c} />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center font-mono text-[13px] text-muted" style={{ color: c.inkMuted }}>
                —
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center gap-1.5 whitespace-nowrap">
                {Icon.flame(12, c.accent)}
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.08em]"
                  style={{ color: c.accent, fontFamily: typ.body }}
                >
                  Hottest
                </span>
              </div>
              <div
                className="text-[18px] leading-tight"
                style={{
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  letterSpacing: typ.displayLetter,
                  color: c.ink,
                }}
              >
                {hottest ? `Number ${hottest.n} is on a roll` : '—'}
              </div>
              <p className="mt-1 text-[12px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                {hottest
                  ? `Drawn ${hottest.count} times in the last ${SIMPLE_STATS_WINDOW} draws${
                      pctAboveAvg !== null
                        ? ` — ${pctAboveAvg >= 0 ? `${pctAboveAvg}% above` : `${Math.abs(pctAboveAvg)}% below`} a rough average for this game.`
                        : '.'
                    }`
                  : loadingBlock
                    ? 'Loading…'
                    : 'No frequency data yet.'}
              </p>
            </div>
          </div>
        </ChanceyCard>

        <ChanceyCard c={c} pad={16}>
          <div className="flex items-center gap-3.5">
            {showSkeleton ? (
              <div className="h-14 w-14 shrink-0 rounded-full" style={shimmerStyle} />
            ) : overdueTop ? (
              <Ball n={overdueTop.n} size={56} muted c={c} />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center font-mono text-[13px]" style={{ color: c.inkMuted }}>
                —
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center gap-1.5 whitespace-nowrap">
                {Icon.clock(12, c.accent3)}
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.08em]"
                  style={{ color: c.accent3, fontFamily: typ.body }}
                >
                  Overdue
                </span>
              </div>
              <div
                className="text-[18px] leading-tight"
                style={{
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  letterSpacing: typ.displayLetter,
                  color: c.ink,
                }}
              >
                {overdueTop ? `Number ${overdueTop.n} hasn’t shown up` : '—'}
              </div>
              <p className="mt-1 text-[12px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                {overdueTop
                  ? `${overdueTop.days} days since its last appearance in this window.`
                  : loadingBlock
                    ? 'Loading…'
                    : 'No overdue list yet.'}
              </p>
            </div>
          </div>
        </ChanceyCard>

        <ChanceyCard c={c} pad={16}>
          <div className="flex items-center gap-3.5">
            <div className="relative flex w-[80px] shrink-0 justify-start">
              {showSkeleton ? (
                <>
                  <div className="h-12 w-12 rounded-full" style={shimmerStyle} />
                </>
              ) : topPair ? (
                <>
                  <Ball n={topPair.a} size={48} c={c} />
                  <div className="-ml-2.5">
                    <Ball n={topPair.b} size={48} c={c} />
                  </div>
                </>
              ) : (
                <span className="font-mono text-[13px]" style={{ color: c.inkMuted }}>
                  —
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center gap-1.5 whitespace-nowrap">
                {Icon.sparkle(12, c.ink)}
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.08em]"
                  style={{ color: c.ink, fontFamily: typ.body }}
                >
                  Top pair
                </span>
              </div>
              <div
                className="text-[18px] leading-tight"
                style={{
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  letterSpacing: typ.displayLetter,
                  color: c.ink,
                }}
              >
                {topPair ? `${topPair.a} & ${topPair.b} stick together` : '—'}
              </div>
              <p className="mt-1 text-[12px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                {topPair
                  ? `Together ${topPair.count} times in this window — lift ×${topPair.lift.toFixed(1)}.`
                  : pairLoading
                    ? 'Loading…'
                    : 'No pairs met the filter.'}
              </p>
            </div>
          </div>
        </ChanceyCard>
      </div>

      <button
        type="button"
        className="py-3.5 text-center text-[12px] font-medium"
        style={{ color: c.inkSoft, fontFamily: typ.body }}
        onClick={() => setPartial({ experienceMode: 'advanced' })}
      >
        Want the full breakdown?{' '}
        <span style={{ color: c.accent, fontWeight: 600 }}>Switch to advanced →</span>
      </button>

      <p
        className="px-5 text-center text-[11px] leading-relaxed"
        style={{ color: c.inkMuted, fontFamily: typ.body }}
      >
        Past draws don&apos;t predict future ones. These insights are for fun, not strategy.
      </p>
    </div>
  );
};
